import createAuth0AuthProvider from './auth0Provider';
import createFirebaseAuthProvider from './firebaseProvider';

// Client-side identity-provider seam (AI4-407), mirroring services/api/src/auth.
//
// A provider implements:
//   name
//   getUser()            -> Promise<{ user_id, email, name, ... } | null>
//   login(redirectPath)  -> Promise<void>   (redirects away)
//   logout()             -> Promise<void>
//   getAuthToken()       -> Promise<string | null>
//   resetClient()        -> void
//
// Both implementations ship in the bundle so the provider is a config choice.
//
// CAVEAT: create-react-app inlines REACT_APP_* at BUILD time, so flipping the
// web provider requires a rebuild — unlike the API, which reads AUTH_PROVIDER
// at runtime. Keep the two in step: an API on firebase with a web bundle still
// built for auth0 will reject every token it is handed.

const PROVIDERS = {
    auth0: createAuth0AuthProvider,
    firebase: createFirebaseAuthProvider,
};

export const DEFAULT_PROVIDER = 'auth0';

export const resolveProviderName = () =>
    (process.env.REACT_APP_AUTH_PROVIDER || DEFAULT_PROVIDER).toLowerCase();

let instance = null;
let instanceFor = null;

export const getAuthProvider = () => {
    const name = resolveProviderName();
    if (instance && instanceFor === name) return instance;
    const factory = PROVIDERS[name];
    if (!factory) {
        throw new Error(
            `[auth] Unknown REACT_APP_AUTH_PROVIDER "${name}". Supported: ${Object.keys(PROVIDERS).join(', ')}.`,
        );
    }
    instance = factory();
    instanceFor = name;
    return instance;
};

export const resetAuthProvider = () => { instance = null; instanceFor = null; };

// Facade with the shape the app already used, so call sites read the same.
const AuthService = {
    get providerName() { return getAuthProvider().name; },
    getUser: (...args) => getAuthProvider().getUser(...args),
    login: (...args) => getAuthProvider().login(...args),
    logout: (...args) => getAuthProvider().logout(...args),
    getAuthToken: (...args) => getAuthProvider().getAuthToken(...args),
    resetClient: (...args) => getAuthProvider().resetClient(...args),
};

export default AuthService;
